import React from 'react';

const RequirementAnalytics = ({ 
  analytics, 
  statusCounts, 
  loading = false 
}) => {
  
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-card border rounded-lg p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-8 bg-muted rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-green-600 bg-green-100',
      in_progress: 'text-blue-600 bg-blue-100',
      partially_filled: 'text-yellow-600 bg-yellow-100',
      completed: 'text-gray-600 bg-gray-100',
      on_hold: 'text-orange-600 bg-orange-100',
      cancelled: 'text-red-600 bg-red-100'
    };
    return colors[status] || colors.open;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-600 bg-gray-100',
      medium: 'text-blue-600 bg-blue-100',
      high: 'text-orange-600 bg-orange-100',
      urgent: 'text-red-600 bg-red-100'
    };
    return colors[priority] || colors.medium;
  };

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="bg-card border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Overview</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Total Requirements</span>
            <span className="text-2xl font-bold text-foreground">
              {analytics?.totalRequirements || 0}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Active</span>
            <span className="text-xl font-semibold text-green-600">
              {statusCounts?.open || 0}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Urgent</span>
            <span className="text-xl font-semibold text-red-600">
              {statusCounts?.urgent || 0}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Completed</span>
            <span className="text-xl font-semibold text-gray-600">
              {statusCounts?.completed || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="bg-card border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Status Distribution</h3>
        <div className="space-y-3">
          {analytics?.statusDistribution ? (
            Object.entries(analytics.statusDistribution).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(status).split(' ')[1]}`}></div>
                  <span className="text-sm capitalize text-muted-foreground">
                    {status.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{count}</span>
                  <div className="w-16 bg-muted rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getStatusColor(status).split(' ')[1]}`}
                      style={{ 
                        width: analytics.totalRequirements > 0 
                          ? `${(count / analytics.totalRequirements) * 100}%` 
                          : '0%' 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">No data available</p>
          )}
        </div>
      </div>

      {/* Priority Distribution */}
      <div className="bg-card border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Priority Levels</h3>
        <div className="space-y-3">
          {analytics?.priorityDistribution ? (
            Object.entries(analytics.priorityDistribution).map(([priority, count]) => (
              <div key={priority} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getPriorityColor(priority).split(' ')[1]}`}></div>
                  <span className="text-sm capitalize text-muted-foreground">
                    {priority}
                  </span>
                </div>
                <span className="text-sm font-medium">{count}</span>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">No data available</p>
          )}
        </div>
      </div>

      {/* Matching Performance */}
      {analytics?.matchingStats && (
        <div className="bg-card border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Matching Performance</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total Matches</span>
              <span className="text-lg font-semibold text-foreground">
                {analytics.matchingStats.totalMatches || 0}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Avg Compatibility</span>
              <span className="text-lg font-semibold text-blue-600">
                {Math.round(analytics.matchingStats.averageScore || 0)}%
              </span>
            </div>
            
            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">Match Status:</span>
              {Object.entries(analytics.matchingStats)
                .filter(([key]) => !['totalMatches', 'averageScore'].includes(key))
                .map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between text-sm">
                    <span className="capitalize text-muted-foreground">
                      {status?.replace('_', ' ')}
                    </span>
                    <span className="font-medium">{count || 0}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Urgent Alerts */}
      {analytics?.urgentRequirements > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-800 mb-2">⚠️ Urgent Alert</h3>
          <p className="text-red-700">
            <span className="font-medium">{analytics.urgentRequirements}</span> requirement
            {analytics.urgentRequirements !== 1 ? 's' : ''} need immediate attention 
            (deadline within 7 days)
          </p>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-card border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
        <div className="space-y-2">
          <button className="w-full text-left p-2 rounded-lg hover:bg-muted transition-colors flex items-center gap-2">
            <span>📊</span>
            <span className="text-sm">View Full Analytics</span>
          </button>
          <button className="w-full text-left p-2 rounded-lg hover:bg-muted transition-colors flex items-center gap-2">
            <span>📈</span>
            <span className="text-sm">Export Report</span>
          </button>
          <button className="w-full text-left p-2 rounded-lg hover:bg-muted transition-colors flex items-center gap-2">
            <span>⚡</span>
            <span className="text-sm">Auto Match All</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequirementAnalytics;